import React, { useState, useMemo, useEffect } from 'react';

import { bemCssModules } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { useStore, userActions, globalActions, notificationsActions } from '@modules';
import { BillingAddressParams } from '@modules/user/types';

import Button from '@components/Button';
import Input from '@components/Input';

import CountrySelect from './Country';
import StateSelect from './State';
import { CHECK_STATE_COUNTRIES } from './constants';
import { useInitialValues } from './useInitialValues';
import validateBillingAddress from './validateBillingAddress';

import styles from './BillingAddress.scss';

const cn = bemCssModules(styles, 'BillingAddress');

export const BillingAddressForm = () => {
  const { t } = useTranslation();
  const [store, dispatch] = useStore();
  const navigate = useNavigate();
  const location = useLocation<LocationStateType['billingAddress']>();

  const { global, user } = store;

  const billingAddress = user.data?.billing_address;

  const [errors, setErrors] = useState<Record<string, string[] | undefined>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  const isLoadingStates = global.loaders.getStates;

  const initialValues = useInitialValues();

  const [state, setState] = useState<BillingAddressParams>(initialValues.values);

  const isDisabled = useMemo(() => {
    const isInvalid = !validateBillingAddress(state, !!global.states?.length);
    if (!billingAddress) return isInvalid;

    return isInvalid || !isChanged;
  }, [state, global?.states.length, isChanged]);

  const onChange = (name: keyof typeof state) => (v: string) => {
    setIsChanged(true);
    setErrors(prev => ({ ...prev, [`billing_address.${name}`]: undefined }));
    setState(prev => ({
      ...prev,
      state_code: name === 'country_code' ? '' : prev.state_code,
      [name]: v,
    }));
  };

  const onChangeInput = (name: keyof typeof state) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(name)(e.target.value);
  };

  const inputProps = (name: keyof typeof state) => ({
    required: true,
    value: state[name],
    onChange: onChangeInput(name),
    error: errors?.[`billing_address.${name}`]?.[0],
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);

    const billing_address = { ...state };

    for (const key in billing_address) {
      if (!billing_address[key]) delete billing_address[key];
    }

    try {
      await dispatch(userActions.editUser({ billing_address })).unwrap();
      await dispatch(userActions.getUserData()).unwrap();

      const notificationData = {
        title: t('common.new_billing_address_notification_done.title'),
        message: t('common.new_billing_address_notification_done.description'),
      };

      dispatch(notificationsActions.add(notificationData));

      if (location.state?.redirectTo) {
        navigate(location.state?.redirectTo);
      }
    } catch (e: any) {
      setErrors(e.data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (CHECK_STATE_COUNTRIES.includes(state.country_code)) {
      dispatch(globalActions.getStates({ country_code: state.country_code }));
    }
  }, [state?.country_code]);

  return (
    <form onSubmit={onSubmit}>
      <CountrySelect
        onChangeValue={onChange('country_code')}
        className={cn('input')}
        placeholder={t('common.select_your_country_title')}
        label={t('common.public_country_title')}
        initialValue={
          initialValues.countrySelect
            ? {
                value: initialValues.countrySelect?.code,
                label: initialValues.countrySelect?.title,
              }
            : undefined
        }
        error={errors?.['billing_address.country_code']?.[0]}
      />

      <Input
        {...inputProps('street_line_1')}
        label={t('common.street_address_1_title')}
        placeholder={t('common.street_address_1_placeholder')}
        required={!state.street_line_2}
        data-testid="billing_address_street_line_1"
      />

      <Input
        {...inputProps('street_line_2')}
        className={cn('input')}
        placeholder={t('common.street_address_2_placeholder')}
        required={!state.street_line_1}
        data-testid="billing_address_street_line_2"
      />

      <div className={cn('input-group')}>
        <Input
          {...inputProps('city')}
          label={t('common.public_city_title')}
          placeholder={t('common.public_city_placeholder')}
          data-testid="billing_address_city"
        />
        <Input
          {...inputProps('zip_code')}
          label={t('common.zip_code')}
          placeholder="000000"
          data-testid="billing_address_zip_code"
        />
      </div>

      {CHECK_STATE_COUNTRIES.includes(state.country_code) && !!global.states?.length && (
        <StateSelect
          onChangeValue={onChange('state_code')}
          className={cn('input-state')}
          placeholder={t('common.select_your_state_title')}
          label={t('common.state_title')}
          initialValue={
            initialValues.stateSelect
              ? { value: initialValues.stateSelect?.code, label: initialValues.stateSelect?.name }
              : undefined
          }
          error={errors?.['billing_address.state_code']?.[0]}
        />
      )}

      <div className={cn('button-group')}>
        <Button
          type="submit"
          className={cn('button')}
          testId="billing_address_submit_button"
          isLoading={isLoading || isLoadingStates}
          disabled={isDisabled || isLoading || isLoadingStates}
        >
          {t('common.button_save')}
        </Button>
      </div>
    </form>
  );
};
